<template>
  <div class="p-none">
    <FilterPanel
      v-model="filter"
      @update="update"
      @create="onCreate"
      @destroy="onDestroy"
      @add="onAdd"
      @remove="onRemove"
      @init-import="
        () => {
          showImportModal = true
        }
      "
    />
    <ItemsTable
      :items="items"
      :total="total"
      :loading="loading"
      class="mt-1"
      @destroy="destroy"
      @clone="clone"
      @edit="edit"
    />
    <Pagination
      class="mt-1"
      :total="total"
      :page="page"
      :limit="paginationLimit"
      @pagination="applyPagination"
    />
    <Modal
      :visible="showModal"
      :loading="loading"
      :mode="mode"
      @close="closeModal"
      @save="onSubmit"
    />
    <ImportModal
      :visible="showImportModal"
      :lists="items"
      @close="
        () => {
          showImportModal = false
        }
      "
      @submit="importList"
    />
  </div>
</template>

<script lang="ts">
import { computed, ref, reactive, defineComponent, onMounted } from 'vue'
import ItemsTable from './SpotsListsTab/ItemsTable.vue'
import FilterPanel from './SpotsListsTab/FilterPanel.vue'
import Modal from './SpotsListsTab/Modal.vue'
import ImportModal from './SpotsListsTab/ImportModal.vue'

import { useSpotsListsStore } from '@/apps/traforama/store/spotsLists'
import {
  ISpotsListsPayload,
  ISpotsListFilter,
  ISpotsListAttributes
} from '@/apps/traforama/interfaces/spotsLists'
import Pagination from '@/components/Pagination.vue'
import { LIMIT } from '@/constants'
import useResize from '@/hooks/useResize'
import useValidation from '@/hooks/useValidation'
import { CampaignValidationErrorsKey } from '@/api/types'
import { safeSubmitProcessing } from '@/utils/helpers'
import { ElMessage } from 'element-plus'

const SAVE_MODE = 'save'
const ADD_MODE = 'add'
const REMOVE_MODE = 'remove'
const CLONE_MODE = 'clone'

export default defineComponent({
  name: 'SpotsListsTab',
  components: {
    ItemsTable,
    FilterPanel,
    Modal,
    ImportModal,
    Pagination
  },
  setup(props, context) {
    const spotsListsStore = useSpotsListsStore()
    const loading = ref(true)
    const page = ref(1)
    const limit = ref(LIMIT)
    const { isDesktop } = useResize()
    const showModal = ref(false)
    const showImportModal = ref(false)
    const mode = ref(SAVE_MODE)

    const defaults = { title: '' }
    const filter = ref({
      ...defaults
    })

    const { provideValidation, setValidationErrors } = useValidation(
      CampaignValidationErrorsKey
    )
    const errors = provideValidation()

    const update = ({
      limit = 15,
      newPage = 1
    }: {
      limit?: number
      newPage?: number
    }) => {
      loading.value = true
      page.value = newPage
      const payload = {
        filter: filter.value,
        limit: limit,
        start: (newPage - 1) * limit
      } as ISpotsListsPayload
      spotsListsStore
        .FetchItems(payload)
        .then((data) => {
          items.value = data || []
          total.value = spotsListsStore.total
          loading.value = false
        })
        .catch(() => {
          loading.value = false
        })
    }

    const load = (id: string) => {
      loading.value = true
      spotsListsStore.FetchItem(id).then(() => {
        loading.value = false
      })
    }

    const items = ref([])
    const total = ref(0)

    const applyPagination = (options: any) => {
      page.value = options.newPage
      limit.value = options.limit
      update({ newPage: page.value })
    }

    const paginationLimit = computed(() => {
      return items.value.length > LIMIT ? items.value.length : LIMIT
    })

    const onCreate = () => {
      mode.value = SAVE_MODE
      spotsListsStore.SetCurrent(null)
      showModal.value = true
    }
    const onDestroy = () => {
      spotsListsStore
        .DestroyItems(Object.keys(spotsListsStore.selected))
        .then(() => {
          ElMessage.success({
            message: 'Spots lists has been archived',
            type: 'success'
          })
          update({ newPage: 1 })
        })
    }
    const onAdd = () => {
      onCreate()
      mode.value = ADD_MODE
    }
    const onRemove = () => {
      onCreate()
      mode.value = REMOVE_MODE
    }

    const destroy = (id: string) => {
      spotsListsStore.DestroyItem(id).then(() => {
        ElMessage.success({
          message: 'Spots list has been archived',
          type: 'success'
        })
        update({ newPage: 1 })
      })
    }

    const edit = (id: string) => {
      loading.value = true
      spotsListsStore.FetchItem(id).then(() => {
        loading.value = false
        showModal.value = true
        mode.value = SAVE_MODE
      })
    }

    const clone = (id: string) => {
      loading.value = true
      spotsListsStore.FetchItem(id).then(() => {
        loading.value = false
        showModal.value = true
        mode.value = CLONE_MODE
      })
    }

    const closeModal = () => {
      showModal.value = false
    }

    const onSuccess = () => {
      closeModal()
      update({ newPage: 1 })
    }

    const importList = (name: string) => {
      ElMessage.success({
        message: 'Spots list was imported successfully',
        type: 'success',
        showClose: true,
        duration: 5000
      })
      update({ newPage: 1 })
    }

    const onSubmit = async (item: ISpotsListAttributes) => {
      errors.value = {}
      if (mode.value === CLONE_MODE) {
        delete item['id']
      }
      let saveFunc = () => spotsListsStore.SaveItem(item)
      if ([ADD_MODE, REMOVE_MODE].includes(mode.value)) {
        saveFunc = () =>
          spotsListsStore.BulkUpdate({
            spot_ids: item.spot_ids,
            mode: mode.value,
            ids: Object.keys(spotsListsStore.selected)
          })
      }
      await safeSubmitProcessing(
        loading,
        saveFunc,
        () => onSuccess(),
        (err) => setValidationErrors(err, errors)
      )
    }

    onMounted(() => {
      update({ newPage: page.value })
    })

    return {
      loading,
      page,
      update,
      items,
      total,
      load,
      filter,
      applyPagination,
      paginationLimit,
      isDesktop,
      showModal,
      showImportModal,
      closeModal,
      importList,
      onCreate,
      onDestroy,
      onAdd,
      onRemove,
      onSubmit,
      destroy,
      clone,
      edit,
      mode
    }
  }
})
</script>
